import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Lock, Eye, EyeOff, Building2, 
  CreditCard, MapPin, FileText, ArrowRight, CheckCircle, AlertCircle 
} from 'lucide-react';

const RegisterAgentPage = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    tax_code: '',
    company_name: '',
    vat_number: '',
    registered_address: '',
    rea_number: '',
    chamber_of_commerce: '',
    certified_email: '',
    password: '',
    confirm_password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Email non valida');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.company_name || !formData.vat_number || !formData.registered_address) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (!/^[0-9]{11}$/.test(formData.vat_number)) {
      toast.error('Partita IVA non valida (11 cifre)');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.rea_number || !formData.chamber_of_commerce || !formData.certified_email || !formData.tax_code) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.certified_email)) {
      toast.error('PEC non valida');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.password || formData.password.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Le password non coincidono');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep4()) return;

    setLoading(true);
    try {
      const response = await axios.post('/auth/register/agent', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        tax_code: formData.tax_code.toUpperCase(),
        company_name: formData.company_name,
        vat_number: formData.vat_number,
        registered_address: formData.registered_address,
        rea_number: formData.rea_number,
        chamber_of_commerce: formData.chamber_of_commerce,
        certified_email: formData.certified_email,
        password: formData.password
      });

      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setStep(5); // Success step
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-xl">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3, 4, 5].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-[#0071e3] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s ? <CheckCircle className="h-4 w-4" /> : s}
                  </div>
                  {s < 5 && (
                    <div className={`w-8 h-1 ${step > s ? 'bg-[#0071e3]' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900 ">
                Registrati come Agente
              </h1>
              <p className="text-gray-600 mt-2">
                {step === 1 && 'Dati personali'}
                {step === 2 && 'Dati aziendali'}
                {step === 3 && 'Dati di registrazione'}
                {step === 4 && 'Crea la password'}
                {step === 5 && 'Registrazione completata'}
              </p>
            </div>

            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Nome *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="first_name"
                        name="first_name"
                        placeholder="Mario"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="agent-first-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Cognome *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Rossi"
                      value={formData.last_name}
                      onChange={handleChange}
                      data-testid="agent-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="mario.rossi@agenzia.it"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+39 333 1234567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-phone"
                    />
                  </div>
                </div>

                <Button 
                  className="w-full btn-primary mt-6"
                  onClick={() => validateStep1() && setStep(2)}
                  data-testid="agent-next-step1"
                >
                  Continua <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Step 2: Company Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Ragione Sociale *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="company_name"
                      name="company_name"
                      placeholder="Agenzia Immobiliare Roma Srl"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-company-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vat_number">Partita IVA *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="vat_number"
                      name="vat_number"
                      placeholder="12345678901"
                      value={formData.vat_number}
                      onChange={handleChange}
                      className="pl-10"
                      maxLength={11}
                      data-testid="agent-vat-number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="registered_address">Sede Legale *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="registered_address"
                      name="registered_address"
                      placeholder="Via Roma 123, 00100 Roma (RM)"
                      value={formData.registered_address}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-address"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-6">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Indietro
                  </Button>
                  <Button 
                    className="flex-1 btn-primary"
                    onClick={() => validateStep2() && setStep(3)}
                    data-testid="agent-next-step2"
                  >
                    Continua <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Registration Info */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tax_code">Codice Fiscale *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="tax_code"
                      name="tax_code"
                      placeholder="RSSMRA80A01H501U"
                      value={formData.tax_code}
                      onChange={handleChange}
                      className="pl-10 uppercase"
                      maxLength={16}
                      data-testid="agent-tax-code"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rea_number">Numero REA *</Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="rea_number"
                        name="rea_number"
                        placeholder="RM-1234567"
                        value={formData.rea_number}
                        onChange={handleChange}
                        className="pl-10"
                        data-testid="agent-rea"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="chamber_of_commerce">Camera di Commercio *</Label>
                    <Input
                      id="chamber_of_commerce"
                      name="chamber_of_commerce"
                      placeholder="CCIAA Roma"
                      value={formData.chamber_of_commerce}
                      onChange={handleChange}
                      data-testid="agent-chamber"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="certified_email">PEC *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="certified_email"
                      name="certified_email"
                      type="email"
                      placeholder="agenzia@pec.it"
                      value={formData.certified_email}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-pec"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-6">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Indietro
                  </Button>
                  <Button 
                    className="flex-1 btn-primary"
                    onClick={() => validateStep3() && setStep(4)}
                    data-testid="agent-next-step3"
                  >
                    Continua <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Password */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimo 8 caratteri"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      data-testid="agent-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Conferma Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Ripeti la password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="agent-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-6">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    Indietro
                  </Button>
                  <Button 
                    className="flex-1 btn-primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="agent-submit"
                  >
                    {loading ? 'Registrazione...' : 'Registrati'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <div className="space-y-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                
                <div>
                  <h3 className="font-semibold text-xl">Registrazione Completata!</h3>
                  <p className="text-gray-600 mt-2">
                    Il tuo account è stato creato con successo.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-[#0071e3] flex-shrink-0 mt-0.5" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-[#1D1D1F]">In attesa di verifica</p>
                      <p className="text-sm text-[#86868B] mt-1">
                        Il tuo account verrà verificato dal nostro team. Riceverai una notifica 
                        via email quando l'account sarà attivato.
                      </p>
                    </div>
                  </div>
                </div>

                <Button 
                  className="w-full btn-primary"
                  onClick={() => navigate('/agent')}
                  data-testid="go-to-dashboard"
                >
                  Vai alla Dashboard
                </Button>
              </div>
            )}

            {step < 5 && (
              <div className="mt-6 text-center text-sm text-gray-600">
                Hai già un account?{' '}
                <Link to="/login" className="text-[#0071e3] font-medium hover:underline">
                  Accedi
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterAgentPage;
